import React, { useState, useEffect } from 'react';
import attendanceAPI from '../api/attendanceAPI';
import SessionCard from './SessionCard';
import { formatDateReadable } from '../utils/dateUtils';
import { MdCalendarToday, MdRefresh } from 'react-icons/md';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';

/**
 * Today's Attendance View
 * Displays all Attendance scheduled for today
 */
function TodaySessions() {
  const { getToken } = useAttendanceAuth();
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTodaySessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const result = await attendanceAPI.getTodaySessions(token);
      
      if (result.success && result.sessions) {
        console.log('Today\'s sessions:', result.sessions);
        // Log tutor info for debugging
        result.sessions.forEach((session, index) => {
          console.log(`Session ${index + 1} (${session.subject || 'N/A'}):`, {
            tutor_name: session.tutor_name,
            tutor: session.tutor,
            teacher_name: session.teacher_name,
            tutor_obj: session.tutor_obj,
            employee: session.employee
          });
        });
        setAttendances(result.sessions);
      } else {
        setAttendances([]);
      }
    } catch (err) {
      console.error('Error fetching today\'s Attendance:', err);
      setError(err.message || 'Failed to load today\'s Attendance');
      setAttendances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodaySessions();
  }, []);

  const today = new Date();
  const todayFormatted = formatDateReadable(today);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading today's Attendance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-red-800 font-semibold mb-1">Error Loading Attendance</h3>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
          <button
            onClick={fetchTodaySessions}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition flex items-center space-x-2"
          >
            <MdRefresh />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <MdCalendarToday className="text-2xl text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">My Attendance Today</h1>
              <p className="text-gray-600 text-sm mt-1">{todayFormatted}</p>
            </div>
          </div>
          <button
            onClick={fetchTodaySessions}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center space-x-2"
            title="Refresh"
          >
            <MdRefresh />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Attendance List */}
      {attendances.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <MdCalendarToday className="text-5xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Attendance Today</h3>
          <p className="text-gray-500">
            You don't have any Attendance scheduled for today.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {attendances.map((session) => (
            <SessionCard key={session.id || session.session_id} session={session} />
          ))}
        </div>
      )}

      {/* Summary */}
      {attendances.length > 0 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">{attendances.length}</span> Attendance{attendances.length !== 1 ? 's' : ''} scheduled for today
          </p>
        </div>
      )}
    </div>
  );
}

export default TodaySessions;

